// ─────────────────────────────────────────────────────────────────────────────
// Damage Calculator Utilities — Pokemon Champions Edition
// - IVs are always 31 (hidden from UI)
// - Stat Points replace EVs: 1:1 ratio, max 32 per stat, max 66 total
// ─────────────────────────────────────────────────────────────────────────────

// All 25 natures with their stat multipliers (1.1 boost / 0.9 penalty)
export const NATURES = {
  Hardy:   { atk: 1,   def: 1,   spa: 1,   spd: 1,   spe: 1   },
  Lonely:  { atk: 1.1, def: 0.9, spa: 1,   spd: 1,   spe: 1   },
  Brave:   { atk: 1.1, def: 1,   spa: 1,   spd: 1,   spe: 0.9 },
  Adamant: { atk: 1.1, def: 1,   spa: 0.9, spd: 1,   spe: 1   },
  Naughty: { atk: 1.1, def: 1,   spa: 1,   spd: 0.9, spe: 1   },
  Bold:    { atk: 0.9, def: 1.1, spa: 1,   spd: 1,   spe: 1   },
  Docile:  { atk: 1,   def: 1,   spa: 1,   spd: 1,   spe: 1   },
  Relaxed: { atk: 1,   def: 1.1, spa: 1,   spd: 1,   spe: 0.9 },
  Impish:  { atk: 1,   def: 1.1, spa: 0.9, spd: 1,   spe: 1   },
  Lax:     { atk: 1,   def: 1.1, spa: 1,   spd: 0.9, spe: 1   },
  Timid:   { atk: 0.9, def: 1,   spa: 1,   spd: 1,   spe: 1.1 },
  Hasty:   { atk: 1,   def: 0.9, spa: 1,   spd: 1,   spe: 1.1 },
  Serious: { atk: 1,   def: 1,   spa: 1,   spd: 1,   spe: 1   },
  Jolly:   { atk: 1,   def: 1,   spa: 0.9, spd: 1,   spe: 1.1 },
  Naive:   { atk: 1,   def: 1,   spa: 1,   spd: 0.9, spe: 1.1 },
  Modest:  { atk: 0.9, def: 1,   spa: 1.1, spd: 1,   spe: 1   },
  Mild:    { atk: 1,   def: 0.9, spa: 1.1, spd: 1,   spe: 1   },
  Quiet:   { atk: 1,   def: 1,   spa: 1.1, spd: 1,   spe: 0.9 },
  Bashful: { atk: 1,   def: 1,   spa: 1,   spd: 1,   spe: 1   },
  Rash:    { atk: 1,   def: 1,   spa: 1.1, spd: 0.9, spe: 1   },
  Calm:    { atk: 0.9, def: 1,   spa: 1,   spd: 1.1, spe: 1   },
  Gentle:  { atk: 1,   def: 0.9, spa: 1,   spd: 1.1, spe: 1   },
  Sassy:   { atk: 1,   def: 1,   spa: 1,   spd: 1.1, spe: 0.9 },
  Careful: { atk: 1,   def: 1,   spa: 0.9, spd: 1.1, spe: 1   },
  Quirky:  { atk: 1,   def: 1,   spa: 1,   spd: 1,   spe: 1   },
};

// Natures that have no +/- effect (neutral)
export const NEUTRAL_NATURES = ['Hardy', 'Docile', 'Serious', 'Bashful', 'Quirky'];

// Natures grouped by boosted stat (for UI display)
export const NATURE_BOOSTS = {
  atk: ['Lonely', 'Adamant', 'Naughty', 'Brave'],
  def: ['Bold', 'Impish', 'Lax', 'Relaxed'],
  spa: ['Modest', 'Mild', 'Rash', 'Quiet'],
  spd: ['Calm', 'Gentle', 'Careful', 'Sassy'],
  spe: ['Timid', 'Hasty', 'Jolly', 'Naive'],
};

// Maps PokeAPI stat names to internal keys
export const STAT_NAME_MAP = {
  hp:               'hp',
  attack:           'atk',
  defense:          'def',
  'special-attack': 'spa',
  'special-defense':'spd',
  speed:            'spe',
};

export const STAT_DISPLAY = {
  hp:  'HP',
  atk: 'Atk',
  def: 'Def',
  spa: 'SpA',
  spd: 'SpD',
  spe: 'Spe',
};

export const STAT_ORDER = ['hp', 'atk', 'def', 'spa', 'spd', 'spe'];

// Max stat points per stat and total (Pokemon Champions rules)
export const MAX_SP_PER_STAT = 32;
export const MAX_SP_TOTAL = 66;

// ─────────────────────────────────────────────────────────────────────────────
// Pokemon Champions Stat Formula
// IVs: always 31
// Stat Points: added directly (1:1) to the final computed stat
// ─────────────────────────────────────────────────────────────────────────────
export function calcStat(statKey, base, level, natureName, statPoints = 0) {
  const nm = NATURES[natureName]?.[statKey] ?? 1;
  if (statKey === 'hp') {
    const raw = Math.floor((2 * base + 31) * level / 100) + level + 10;
    return raw + statPoints;
  }
  return Math.floor((Math.floor((2 * base + 31) * level / 100) + 5 + statPoints) * nm);
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat stage multipliers: -6 to +6
// Standard Gen formula: (2 + |boost|) / 2 for positive, 2 / (2 + |boost|) for negative
// ─────────────────────────────────────────────────────────────────────────────
export function getBoostMultiplier(boost) {
  const b = Math.max(-6, Math.min(6, boost));
  if (b >= 0) return (2 + b) / 2;
  return 2 / (2 + Math.abs(b));
}

// Apply boost stage (and optional extra multiplier, e.g. burn penalty)
export function applyBoost(stat, boost, extraMultiplier = 1) {
  return Math.max(1, Math.floor(stat * getBoostMultiplier(boost) * extraMultiplier));
}

// ─────────────────────────────────────────────────────────────────────────────
// Type effectiveness — uses the type_chart.json format
// typeChart: { attackingType: { defendingType: multiplier } }
// ─────────────────────────────────────────────────────────────────────────────
export function getTypeEffectiveness(moveType, defenderTypes, typeChart) {
  if (!moveType || !defenderTypes?.length) return 1;
  let multiplier = 1;
  const mt = moveType.toLowerCase();
  for (const dt of defenderTypes) {
    const row = typeChart[mt] ?? {};
    multiplier *= row[dt.toLowerCase()] ?? 1;
  }
  return multiplier;
}

// ─────────────────────────────────────────────────────────────────────────────
// Weather damage multiplier for a given move type
// ─────────────────────────────────────────────────────────────────────────────
export function getWeatherMultiplier(weather, moveType) {
  const t = moveType?.toLowerCase();
  if (weather === 'Sun')  return t === 'fire' ? 1.5 : t === 'water' ? 0.5 : 1;
  if (weather === 'Rain') return t === 'water' ? 1.5 : t === 'fire' ? 0.5 : 1;
  return 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Terrain damage multiplier (attacker must be grounded)
// ─────────────────────────────────────────────────────────────────────────────
export function getTerrainMultiplier(terrain, moveType, attackerGrounded = true) {
  if (!attackerGrounded) return 1;
  const t = moveType?.toLowerCase();
  if (terrain === 'Electric' && t === 'electric') return 1.3;
  if (terrain === 'Grassy'   && t === 'grass')    return 1.3;
  if (terrain === 'Psychic'  && t === 'psychic')  return 1.3;
  if (terrain === 'Misty'    && t === 'dragon')   return 0.5;
  return 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// STAB multiplier
// ─────────────────────────────────────────────────────────────────────────────
export function getStabMultiplier(moveType, pokemonTypes, adaptability = false, teraType = null) {
  const mt = moveType?.toLowerCase();
  // Terastal STAB: tera type matches move type
  if (teraType) {
    const originalMatch = pokemonTypes.some(t => t.toLowerCase() === mt);
    const teraMatch = teraType.toLowerCase() === mt;
    if (teraMatch && originalMatch) return adaptability ? 2.25 : 2;
    if (teraMatch)                  return adaptability ? 2    : 1.5;
  }
  const hasStab = pokemonTypes.some(t => t.toLowerCase() === mt);
  if (!hasStab) return 1;
  return adaptability ? 2 : 1.5;
}

// ─────────────────────────────────────────────────────────────────────────────
// Item helpers — returns separate objects for attacker and defender
// ─────────────────────────────────────────────────────────────────────────────
export function getItemAtkMultiplier(item, moveCategory, moveType) {
  const t = moveType?.toLowerCase();
  switch (item) {
    case 'Life Orb':    return 1.3;
    case 'Choice Band': return moveCategory === 'Physical' ? 1.5 : 1;
    case 'Choice Specs':return moveCategory === 'Special'  ? 1.5 : 1;
    case 'Muscle Band': return moveCategory === 'Physical' ? 1.1 : 1;
    case 'Wise Glasses':return moveCategory === 'Special'  ? 1.1 : 1;
    case 'Silk Scarf':  return t === 'normal'   ? 1.2 : 1;
    case 'Charcoal':    return t === 'fire'      ? 1.2 : 1;
    case 'Mystic Water':return t === 'water'     ? 1.2 : 1;
    case 'Magnet':      return t === 'electric'  ? 1.2 : 1;
    case 'Miracle Seed':return t === 'grass'     ? 1.2 : 1;
    case 'NeverMeltIce':return t === 'ice'       ? 1.2 : 1;
    case 'Black Belt':  return t === 'fighting'  ? 1.2 : 1;
    case 'Sharp Beak':  return t === 'flying'    ? 1.2 : 1;
    case 'Poison Barb': return t === 'poison'    ? 1.2 : 1;
    case 'Soft Sand':   return t === 'ground'    ? 1.2 : 1;
    case 'Hard Stone':  return t === 'rock'      ? 1.2 : 1;
    case 'Silver Powder':return t === 'bug'      ? 1.2 : 1;
    case 'Spell Tag':   return t === 'ghost'     ? 1.2 : 1;
    case 'TwistedSpoon':return t === 'psychic'   ? 1.2 : 1;
    case 'Dragon Fang': return t === 'dragon'    ? 1.2 : 1;
    case 'Black Glasses':return t === 'dark'     ? 1.2 : 1;
    case 'Metal Coat':  return t === 'steel'     ? 1.2 : 1;
    case 'Fairy Feather':return t === 'fairy'    ? 1.2 : 1;
    default: return 1;
  }
}

export function getItemDefMultiplier(item, moveCategory) {
  if (item === 'Assault Vest' && moveCategory === 'Special') return 1 / 1.5;
  if (item === 'Eviolite') return 1 / 1.5; // both def and spd
  return 1;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ability helpers
// ─────────────────────────────────────────────────────────────────────────────
export function getAbilityAtkStatMultiplier(ability, moveCategory, weather = 'None') {
  if (ability === 'Huge Power' || ability === 'Pure Power') return moveCategory === 'Physical' ? 2 : 1;
  if (ability === 'Hustle')       return moveCategory === 'Physical' ? 1.5 : 1;
  if (ability === 'Solar Power' && weather === 'Sun') return moveCategory === 'Special' ? 1.5 : 1;
  if (ability === 'Guts')         return moveCategory === 'Physical' ? 1.5 : 1; // simplified: assume status
  if (ability === 'Gorilla Tactics') return moveCategory === 'Physical' ? 1.5 : 1;
  return 1;
}

export function getAbilityDmgMultiplier(ability, moveType, moveCategory, isContact, isPunch, typeEffectiveness, movePower, weather = 'None', attackerHpPct = 100) {
  const t = moveType?.toLowerCase();
  switch (ability) {
    case 'Tough Claws':   return isContact ? 1.3 : 1;
    case 'Unseen Fist':   return isContact ? 1.0 : 1; // bypasses protect, no dmg bonus
    case 'Iron Fist':     return isPunch   ? 1.2 : 1;
    case 'Sheer Force':   return 1.3; // simplified: assume move has secondary effect
    case 'Technician':    return movePower <= 60 ? 1.5 : 1;
    case 'Analytic':      return 1.3; // simplified: assume moves last
    case 'Neuroforce':    return typeEffectiveness > 1 ? 1.25 : 1;
    case 'Blaze':         return (t === 'fire'  && attackerHpPct <= 33) ? 1.5 : 1;
    case 'Torrent':       return (t === 'water' && attackerHpPct <= 33) ? 1.5 : 1;
    case 'Overgrow':      return (t === 'grass' && attackerHpPct <= 33) ? 1.5 : 1;
    case 'Swarm':         return (t === 'bug'   && attackerHpPct <= 33) ? 1.5 : 1;
    case 'Flash Fire':    return t === 'fire'     ? 1.5 : 1; // activated via Flash Fire toggle, not HP
    // Weather-dependent damage abilities
    case 'Sand Force':
      return weather === 'Sand' && (t === 'rock' || t === 'ground' || t === 'steel') ? 1.3 : 1;
    case 'Reckless':
      return 1.2; // simplified: assume move has recoil
    case 'Strong Jaw':
      return ['dark','dragon','electric','fairy','fire','ghost','ice','normal','poison','psychic','rock','steel','water'].includes(t) ? 1.5 : 1; // biting moves simplified
    case 'Mega Launcher':
      return 1.5; // simplified: assume move is pulse/aura
    case 'Punk Rock':
      return moveCategory === 'Special' ? 1.3 : 1; // simplified: assume sound move
    case 'Transistor':    return t === 'electric' ? 1.5 : 1;
    case 'Dragon\'s Maw': return t === 'dragon'   ? 1.5 : 1;
    case 'Rocky Payload': return t === 'rock'     ? 1.5 : 1;
    case 'Steely Spirit': return t === 'steel'    ? 1.5 : 1;
    case 'Stakeout':      return 2.0; // simplified: assume target switched in
    case 'Adaptability':  return 1; // handled via STAB multiplier
    default:              return 1;
  }
}

export function getAbilityDefMultiplier(ability, moveCategory, moveType, typeEffectiveness, defenderGrounded, defenderHpPct = 100) {
  const t = moveType?.toLowerCase();
  switch (ability) {
    case 'Fur Coat':            return moveCategory === 'Physical' ? 0.5  : 1;
    case 'Multiscale':
    case 'Shadow Shield':       return defenderHpPct === 100 ? 0.5 : 1;
    case 'Filter':
    case 'Solid Rock':
    case 'Prism Armor':         return typeEffectiveness > 1 ? 0.75 : 1;
    case 'Thick Fat':           return (t === 'fire' || t === 'ice') ? 0.5 : 1;
    case 'Levitate':            return (t === 'ground' && defenderGrounded) ? 0 : 1;
    case 'Flash Fire (defender)':return t === 'fire'     ? 0 : 1;
    case 'Storm Drain':         return t === 'water'    ? 0 : 1;
    case 'Lightning Rod':
    case 'Motor Drive':         return t === 'electric' ? 0 : 1;
    case 'Water Absorb':
    case 'Volt Absorb':         return (t === 'water' || t === 'electric') ? 0 : 1;
    case 'Sap Sipper':          return t === 'grass'    ? 0 : 1;
    case 'Wonder Guard':        return typeEffectiveness <= 1 ? 0 : 1;
    default:                    return 1;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main damage calculation
// Returns array of 16 damage rolls (random factor 85–100)
// Returns null for status moves or 0-power moves
// ─────────────────────────────────────────────────────────────────────────────
export function calculateDamageRolls({
  attackerLevel = 50,
  movePower = 0,
  moveCategory = 'Physical',
  moveType = 'Normal',
  atkStat,                  // final modified attacking stat
  defStat,                  // final modified defending stat
  isCritical = false,
  stabMultiplier = 1,
  typeEffectiveness = 1,
  weatherMultiplier = 1,
  terrainMultiplier = 1,
  burnMultiplier = 1,
  itemAtkMultiplier = 1,
  abilityDmgMultiplier = 1,
  abilityDefMultiplier = 1,
  screenMultiplier = 1,
  isSpread = false,
}) {
  if (movePower <= 0 || moveCategory === 'Status') return null;

  // Base calculation (floor at each step per game formula)
  let base = Math.floor(
    Math.floor(
      Math.floor(2 * attackerLevel / 5 + 2) * movePower * atkStat / defStat / 50
    ) + 2
  );

  // Doubles spread penalty
  if (isSpread) base = Math.floor(base * 0.75);

  // Weather modifier
  base = Math.floor(base * weatherMultiplier);

  // Critical hit (1.5x in Gen 6+)
  if (isCritical) base = Math.floor(base * 1.5);

  // Defender ability (multiplicative on damage received, applied to base)
  base = Math.floor(base * abilityDefMultiplier);

  // Screens (Reflect / Light Screen / Aurora Veil) — not applied on crits
  if (!isCritical) base = Math.floor(base * screenMultiplier);

  // 16 random rolls (85/100 … 100/100) with remaining modifiers
  const rolls = [];
  for (let r = 85; r <= 100; r++) {
    let dmg = Math.floor(base * r / 100);
    dmg = Math.floor(dmg * stabMultiplier);
    dmg = Math.floor(dmg * typeEffectiveness);
    dmg = Math.floor(dmg * terrainMultiplier);
    dmg = Math.floor(dmg * burnMultiplier);
    dmg = Math.floor(dmg * itemAtkMultiplier);
    dmg = Math.floor(dmg * abilityDmgMultiplier);
    rolls.push(Math.max(1, dmg));
  }

  return rolls;
}

// ─────────────────────────────────────────────────────────────────────────────
// Optimal Defensive SP
// Finds the minimum (hpSP + defSP) such that the worst-case damage roll
// is strictly less than the defender's final HP.
//
// Parameters mirror the inputs needed by calculateDamageRolls, plus:
//   baseHP      — defender's base HP stat
//   baseDefStat — defender's base defensive stat (Def or SpD, before SP)
//   defStatKey  — 'def' or 'spd'
//   defNature   — defender's nature name
//   defLevel    — defender's level
//   currentSPOthers — total SP already spent on other stats (≠ hp, ≠ defStatKey)
//   itemDefMult — multiplier from item on the defensive stat (e.g. Eviolite 1.5)
//   defBoost    — stage boost on the defensive stat
//
// Returns { hpSP, defSP, totalSP } or null if impossible even at max SP.
// ─────────────────────────────────────────────────────────────────────────────
export function findOptimalDefensiveSP({
  baseHP, baseDefStat, defStatKey, defNature, defLevel,
  currentSPOthers,
  itemDefMult = 1,
  defBoost = 0,
  // damage params (attacker side, already fixed)
  attackerLevel, movePower, moveCategory, moveType,
  atkStat,
  isCritical, stabMultiplier, typeEffectiveness,
  weatherMultiplier, terrainMultiplier,
  itemAtkMultiplier, abilityDmgMultiplier, abilityDefMultiplier,
  screenMultiplier, isSpread,
}) {
  const spBudget = MAX_SP_TOTAL - currentSPOthers;
  const maxHP  = Math.min(MAX_SP_PER_STAT, spBudget);
  const maxDef = Math.min(MAX_SP_PER_STAT, spBudget);

  // Base HP stat (0 SP)
  const hpBase0 = Math.floor((2 * baseHP + 31) * defLevel / 100) + defLevel + 10;

  let best = null;

  for (let hpSP = 0; hpSP <= maxHP; hpSP++) {
    const finalHP = hpBase0 + hpSP;
    const maxDefSP = Math.min(maxDef, spBudget - hpSP);

    for (let dSP = 0; dSP <= maxDefSP; dSP++) {
      const nm = NATURES[defNature]?.[defStatKey] ?? 1;
      const defStatRaw = Math.floor((Math.floor((2 * baseDefStat + 31) * defLevel / 100) + 5 + dSP) * nm);
      const defStatWithItem = Math.floor(defStatRaw * itemDefMult);
      const effectiveDef = Math.max(1, Math.floor(defStatWithItem * (defBoost >= 0 ? (2 + defBoost) / 2 : 2 / (2 + Math.abs(defBoost)))));

      const rolls = calculateDamageRolls({
        attackerLevel, movePower, moveCategory, moveType,
        atkStat, defStat: effectiveDef,
        isCritical, stabMultiplier, typeEffectiveness,
        weatherMultiplier, terrainMultiplier,
        burnMultiplier: 1, itemAtkMultiplier,
        abilityDmgMultiplier, abilityDefMultiplier,
        screenMultiplier, isSpread,
      });

      if (!rolls) return null;

      const maxDmg = rolls[rolls.length - 1];
      if (maxDmg < finalHP) {
        const total = hpSP + dSP;
        if (best === null || total < best.totalSP) {
          best = { hpSP, defSP: dSP, totalSP: total };
        }
        // Can't do better with this hpSP by increasing dSP
        break;
      }
    }
  }

  return best;
}

// ─────────────────────────────────────────────────────────────────────────────
// KO Probability
// Returns probability of a KO given the rolls and defender's max HP
// ─────────────────────────────────────────────────────────────────────────────
// Calculates the probability (0–100) that n hits from the given roll distribution KO the defender.
// Uses brute-force iteration: 16^n combinations (cheap for n≤3, capped at 3).
function calcNHKOPct(rolls, hp, n) {
  if (n === 1) {
    return (rolls.filter(r => r >= hp).length / rolls.length) * 100;
  }
  if (n === 2) {
    let ko = 0;
    for (const r1 of rolls) for (const r2 of rolls) if (r1 + r2 >= hp) ko++;
    return (ko / (rolls.length * rolls.length)) * 100;
  }
  if (n === 3) {
    let ko = 0;
    for (const r1 of rolls) for (const r2 of rolls) for (const r3 of rolls) if (r1 + r2 + r3 >= hp) ko++;
    return (ko / (rolls.length ** 3)) * 100;
  }
  // For 4HKO+: approximate — if minHitsToKO === n it's always guaranteed
  return (Math.ceil(hp / rolls[rolls.length - 1]) === n) ? 100 : null;
}

export function getKOInfo(rolls, defenderHP) {
  if (!rolls || !defenderHP) return null;
  const koCount = rolls.filter(r => r >= defenderHP).length;
  const pct = (koCount / rolls.length) * 100;

  // Damage % range
  const minPct = ((rolls[0]  / defenderHP) * 100).toFixed(1);
  const maxPct = ((rolls[rolls.length - 1] / defenderHP) * 100).toFixed(1);

  // How many hits to KO
  const minRoll = rolls[0];
  const maxRoll = rolls[rolls.length - 1];
  const minHitsToKO = Math.ceil(defenderHP / maxRoll);
  const maxHitsToKO = Math.ceil(defenderHP / minRoll);

  let koLabel = '';
  if (koCount === 16) {
    koLabel = 'Guaranteed OHKO';
  } else if (koCount > 0) {
    // Partial OHKO — show probability
    koLabel = `${pct.toFixed(1)}% chance to OHKO`;
  } else {
    // Multi-hit KO — always compute and show probability
    const n = minHitsToKO;
    const nPct = calcNHKOPct(rolls, defenderHP, n);
    if (nPct === null) {
      koLabel = `${n}-${maxHitsToKO}HKO`;
    } else if (nPct >= 100) {
      koLabel = `Guaranteed ${n}HKO`;
    } else {
      koLabel = `${nPct.toFixed(1)}% chance to ${n}HKO`;
    }
  }

  return { minPct, maxPct, koCount, pct, koLabel, minHitsToKO, maxHitsToKO };
}
